import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, Sparkles, ArrowRight, Radio, Loader2, Package } from 'lucide-react';
import { CreateTypeGrid } from '@/components/creation-flow/CreateTypeCards';
import { classifyListing, fetchCreatorCapabilities, type ListingClassification } from '@/services/creatorApi';

// The shared first step every create path routes through. The AI field
// accelerates — describe it in a sentence and we classify + prefill — but it
// NEVER blocks: the three tiles below always route straight to their builder.
const TYPE_META: Record<'webinar' | 'course' | 'video' | 'series', { label: string; route: string }> = {
  webinar: { label: 'Featured event', route: '/my-store/new/webinar' },
  course: { label: 'On-demand course', route: '/my-store/course/new' },
  video: { label: 'Video, show or series', route: '/my-store/new/video' },
  series: { label: 'series to develop', route: '/my-store/series-studio' },
};

const ListingChooserPage: React.FC = () => {
  const navigate = useNavigate();
  const [prompt, setPrompt] = useState('');
  // Bundles and season passes: founding Studio partners only (the server enforces it too).
  const [canBundle, setCanBundle] = useState(false);
  useEffect(() => { let alive = true; fetchCreatorCapabilities().then(c => { if (alive) setCanBundle(!!c.bundleMembership); }).catch(() => {}); return () => { alive = false; }; }, []);
  const [classifying, setClassifying] = useState(false);
  const [result, setResult] = useState<ListingClassification | null>(null);
  const [noMatch, setNoMatch] = useState(false);

  const runClassify = async () => {
    const text = prompt.trim();
    if (!text || classifying) return;
    setClassifying(true);
    setResult(null);
    setNoMatch(false);
    const res = await classifyListing(text);
    setClassifying(false);
    if (res.type) setResult(res);
    else setNoMatch(true);
  };

  const continueToBuilder = () => {
    if (!result?.type) return;
    const meta = TYPE_META[result.type];
    navigate(meta.route, {
      // Always carry the creator's own words (`idea`) — even if classification
      // came back thin, the builder/concierge can pick up from what they typed.
      state: { title: result.title, description: result.description, priceCents: result.suggestedPriceCents, idea: prompt.trim() },
    });
  };

  const typeLabel = result?.type ? TYPE_META[result.type].label : '';
  const suggested = result?.suggestedPriceCents != null
    ? (result.suggestedPriceCents === 0 ? 'free' : `$${(result.suggestedPriceCents / 100).toFixed(0)}`)
    : null;

  return (
    <main className="min-h-screen" style={{ background: 'var(--hj-bg)', color: 'var(--hj-ink)' }}>
      {/* Top bar */}
      <div className="sticky top-0 z-10" style={{ background: 'var(--hj-bg)', borderBottom: '1px solid var(--hj-hairline-soft)' }}>
        <div className="mx-auto flex items-center gap-3" style={{ maxWidth: 1120, padding: '14px 16px' }}>
          <button
            type="button"
            onClick={() => navigate('/my-store')}
            aria-label="Back to My Store"
            className="flex-shrink-0 inline-flex items-center justify-center"
            style={{ width: 36, height: 36, borderRadius: 999, background: 'var(--hj-surface-2)', border: '1px solid var(--hj-hairline)', color: 'var(--hj-ink-2)', cursor: 'pointer' }}
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
          <div className="mono-label" style={{ color: 'var(--hj-ink-3)' }}>My Store · New listing</div>
        </div>
      </div>

      {/* Content */}
      <div className="mx-auto" style={{ maxWidth: 640, padding: '48px 16px 96px' }}>
        <div className="text-center">
          <h1 className="font-hj-display m-0" style={{ fontSize: 'clamp(28px, 4.6vw, 40px)', color: 'var(--hj-ink)', letterSpacing: '-0.025em', fontWeight: 800, lineHeight: 1.05 }}>
            What do you want to sell?
          </h1>
          <p className="m-0" style={{ marginTop: 12, fontSize: 15, color: 'var(--hj-ink-2)', lineHeight: 1.55 }}>
            Describe it in a sentence and we'll set up the right kind of listing — or pick one below.
          </p>
        </div>

        {/* AI field */}
        <div
          data-paper="beige"
          style={{ marginTop: 28, padding: 18, borderRadius: 'var(--hj-r-md)', background: 'var(--hj-beige)', border: '1px solid #ffd600', boxShadow: '0 0 0 3px rgb(255 214 0 / 0.14)', color: 'var(--hj-beige-ink)' }}
        >
          <div className="flex items-start gap-3">
            <span className="inline-flex items-center justify-center flex-shrink-0" style={{ width: 34, height: 34, borderRadius: 9, background: 'var(--hj-yellow-tint)', color: '#9a7d05' }}>
              <Sparkles className="w-4 h-4" />
            </span>
            <textarea
              value={prompt}
              onChange={e => setPrompt(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) runClassify(); }}
              rows={2}
              placeholder="I want to run a live Q&A for agents on booking commercial work, next month…"
              style={{ flex: 1, resize: 'none', border: 0, outline: 'none', background: 'transparent', color: 'var(--hj-beige-ink)', fontSize: 15, lineHeight: 1.5, fontFamily: 'inherit', paddingTop: 6 }}
            />
          </div>
          <div className="flex items-center justify-between gap-3" style={{ marginTop: 14 }}>
            <span className="mono-label" style={{ color: 'var(--hj-beige-ink-3)' }}>Optional · you can skip and pick below</span>
            <button
              type="button"
              onClick={runClassify}
              disabled={!prompt.trim() || classifying}
              className="inline-flex items-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ padding: '8px 16px', borderRadius: 999, background: 'var(--hj-blue)', border: '1px solid var(--hj-blue)', color: 'oklch(0.18 0.005 70)', fontSize: 13, fontWeight: 700, fontFamily: 'inherit', cursor: 'pointer', minHeight: 38 }}
            >
              {classifying ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
              Set it up
              {!classifying && <ArrowRight className="w-4 h-4" />}
            </button>
          </div>
        </div>

        {/* AI result callout */}
        {result?.type && (
          <div
            className="flex items-center gap-3"
            style={{ marginTop: 14, padding: 16, borderRadius: 'var(--hj-r-md)', background: 'var(--hj-pink-tint)', border: '1px solid rgb(242 134 201 / 0.45)' }}
          >
            <span className="inline-flex items-center justify-center flex-shrink-0" style={{ width: 34, height: 34, borderRadius: 9, background: 'oklch(0.18 0.005 70)', color: 'var(--hj-pink)' }}>
              <Radio className="w-4 h-4" />
            </span>
            <p className="m-0 flex-1" style={{ fontSize: 13.5, color: 'var(--hj-ink)', lineHeight: 1.5 }}>
              Sounds like a <strong style={{ color: '#f286c9' }}>{typeLabel}</strong>. We'll pre-fill the title, a draft description{suggested ? <>, and suggest <strong>{suggested}</strong></> : null}.
            </p>
            <button
              type="button"
              onClick={continueToBuilder}
              className="inline-flex items-center gap-1.5 flex-shrink-0"
              style={{ padding: '8px 14px', borderRadius: 999, background: 'transparent', border: '1px solid var(--hj-hairline)', color: 'var(--hj-ink)', fontSize: 13, fontWeight: 600, fontFamily: 'inherit', cursor: 'pointer', minHeight: 36 }}
            >
              Continue <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        )}
        {noMatch && (
          <p className="m-0 text-center" style={{ marginTop: 14, fontSize: 13, color: 'var(--hj-ink-2)' }}>
            Couldn't tell from that — pick a type below.
          </p>
        )}

        {/* Divider */}
        <div className="flex items-center gap-3" style={{ margin: '32px 0 18px' }}>
          <div style={{ flex: 1, height: 1, background: 'var(--hj-hairline-soft)' }} />
          <span className="mono-label" style={{ color: 'var(--hj-ink-4)' }}>or start from a type</span>
          <div style={{ flex: 1, height: 1, background: 'var(--hj-hairline-soft)' }} />
        </div>

        <CreateTypeGrid hrefMode="type" />

        {/* Products: a physical thing that ships, or a download. Open to every creator. */}
        <button
          type="button"
          onClick={() => navigate('/my-store/new/product')}
          className="w-full flex items-center gap-3 text-left"
          style={{ marginTop: 14, padding: 16, borderRadius: 'var(--hj-r-md)', background: 'var(--hj-surface)', border: '1px solid var(--hj-hairline)', color: 'var(--hj-ink)', cursor: 'pointer', fontFamily: 'inherit' }}
        >
          <span className="inline-flex items-center justify-center flex-shrink-0" style={{ width: 34, height: 34, borderRadius: 9, background: 'var(--hj-yellow-tint)', color: '#9a7d05' }}>
            <Package className="w-4 h-4" />
          </span>
          <span className="flex-1 min-w-0">
            <span className="block" style={{ fontSize: 15, fontWeight: 700 }}>Product</span>
            <span className="block" style={{ fontSize: 13, color: 'var(--hj-ink-2)', lineHeight: 1.45 }}>A T-shirt, a poster, a book you ship, or a download like a script pack. No commission.</span>
          </span>
          <ArrowRight className="w-4 h-4 flex-shrink-0" />
        </button>

        {canBundle && (
        <button
          type="button"
          onClick={() => navigate('/my-store/bundle/new')}
          className="w-full flex items-center gap-3 text-left"
          style={{ marginTop: 14, padding: 16, borderRadius: 'var(--hj-r-md)', background: 'var(--hj-surface)', border: '1px solid var(--hj-hairline)', color: 'var(--hj-ink)', cursor: 'pointer', fontFamily: 'inherit', minHeight: 56 }}
        >
          <span className="inline-flex items-center justify-center flex-shrink-0" style={{ width: 34, height: 34, borderRadius: 9, background: 'var(--hj-yellow-tint)', color: '#9a7d05' }}>
            <Package className="w-4 h-4" />
          </span>
          <span className="flex-1 min-w-0">
            <span className="block" style={{ fontSize: 15, fontWeight: 700 }}>Bundle or season pass</span>
            <span className="block" style={{ fontSize: 13, color: 'var(--hj-ink-2)', lineHeight: 1.45 }}>Sell listings you already have together at one price.</span>
          </span>
          <ArrowRight className="w-4 h-4 flex-shrink-0" />
        </button>
        )}
      </div>
    </main>
  );
};

export default ListingChooserPage;
